// دریافت و پرداخت
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ListPage, { ui } from '../../components/ListPage';
import { usePager, CountBadge } from '../../components/paging';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import { useFormButtons } from '../../utils/vbPerms';
import { jsonOf, netErr } from '../../utils/http';

const toFa   = n => String(n ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const fmtNum = n => (n ? Number(n).toLocaleString('fa-IR') : '');

const API = (path, opts = {}) => fetch(`/api/inout${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

const EMPTY = () => ({
  no: '', sayad: '', in_amount: '', out_amount: '',
  daftar_date: '', bank_date: '', remark: '', remark2: '',
  financial_year: '', sanad_no: '', sanad_date: '',
  is_active: 1, is_final: 0, status: 0,
  vouch_group_id: 0, vouch_group: '', vouch_group2_id: 0, vouch_group2: '',
  credit_id: 0, credit: '', income_id: 0, income: '',
});

function Lookup({ label, value, endpoint, onPick, flex = 1 }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const search = async (text) => {
    setQ(text);
    const j = await API(`${endpoint}${endpoint.includes('?') ? '&' : '?'}q=${encodeURIComponent(text)}`);
    if (j.ok) { setRows(j.rows || []); setOpen(true); }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex, position: 'relative' }}>
      <label style={ui.mlabel}>{label}</label>
      <input value={value || q} onFocus={() => search(q)} onChange={e => search(e.target.value)}
        placeholder="جستجو..." style={ui.input} />
      {value && <button onClick={() => { onPick({ id: 0, display: '' }); setQ(''); }}
        style={{ position: 'absolute', left: '6px', top: '30px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#c62828', fontSize: '14px' }}>✕</button>}
      {open && rows.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', right: 0, left: 0, background: '#fff', border: '1px solid #e0e0e0',
          borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', zIndex: 700, maxHeight: '220px', overflowY: 'auto', marginTop: '2px' }}>
          {rows.map(r => (
            <div key={r.id} onClick={() => { onPick(r); setOpen(false); setQ(''); }}
              style={{ padding: '8px 10px', cursor: 'pointer', fontSize: '12px', borderBottom: '1px solid #f0f0f0' }}
              onMouseEnter={e => e.currentTarget.style.background = '#e8f0fe'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}>{r.display}</div>
          ))}
        </div>
      )}
    </div>
  );
}

/* انتخاب حساب بانکی — صفحه بدون حساب باز می‌شود و ابتدا حساب انتخاب می‌شود */
function AccountPicker({ onPick }) {
  const [q, setQ]       = useState('');
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (text = '') => {
    setBusy(true);
    const j = await API(`/accounts/?q=${encodeURIComponent(text)}`);
    setBusy(false);
    if (j.ok) setRows(j.rows || []);
  }, []);
  useEffect(() => { load(''); }, [load]);

  return (
    <div style={{ padding: '28px 20px', maxWidth: '820px', margin: '0 auto' }}>
      <div style={{ fontSize: '15px', fontWeight: 800, color: '#1a2f50', marginBottom: '10px' }}>
        ابتدا حساب بانکی را انتخاب کنید
      </div>
      <input value={q} onChange={e => { setQ(e.target.value); load(e.target.value); }}
        placeholder="جستجوی شماره یا عنوان حساب..." style={{ ...ui.input, width: '100%', marginBottom: '12px' }} />
      {busy && <div style={{ padding: '10px' }}>⏳ در حال بارگذاری...</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: '10px' }}>
        {rows.map(r => (
          <div key={r.id} onClick={() => onPick(r)}
            style={{ border: '1px solid #e0e0e0', borderRadius: '10px', padding: '10px 12px', cursor: 'pointer', background: '#fff' }}
            onMouseEnter={e => e.currentTarget.style.background = '#e8f0fe'}
            onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
            <div style={{ fontWeight: 700, fontSize: '13px', color: '#1a2f50' }}>{r.title}</div>
            <div style={{ fontFamily: 'monospace', fontSize: '12px', color: '#555' }}>{toFa(r.no)}</div>
            <div style={{ fontSize: '11px', color: '#888' }}>{r.bank} {r.owner ? `— ${r.owner}` : ''}</div>
          </div>
        ))}
      </div>
      {!busy && rows.length === 0 && <div style={ui.empty}>حسابی یافت نشد</div>}
    </div>
  );
}

export default function InOutList({ user, onBack }) {
  // دکمه‌های فرم FRMINOUTNEW — دیدن = دسترسی
  const vbf = useFormButtons('FRMINOUTNEW', ["BTNNEW","BTNEDIT","BTNDELETE","BTNOK"]);
  const [account, setAccount]   = useState(null);   // {id,no,title,...}
  const [info, setInfo]         = useState(null);   // هدر حساب
  const [opts, setOpts]         = useState({ status: [], filters: [], today: '' });

  const [data, setData]         = useState([]);
  const pager = usePager();          // صفحه‌بندی مشترک — ۲۰۰ ردیف در هر صفحه
  const [sums, setSums]         = useState(null);

  const [summary, setSummary]   = useState(false);  // خلاصه
  const [filterIdx, setFilterIdx] = useState(0);    // فیلتر انتخاب‌شده
  const [currentDate, setCurrentDate] = useState('');
  const [startYear, setStartYear] = useState('');
  const [endYear, setEndYear]     = useState('');
  const [fromDaftar, setFromDaftar] = useState('');
  const [toDaftar, setToDaftar]     = useState('');
  const [fromBank, setFromBank]     = useState('');
  const [toBank, setToBank]         = useState('');
  const [fromNo, setFromNo]         = useState('');
  const [toNo, setToNo]             = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [search, setSearch]     = useState('');
  const [selected, setSelected] = useState(null);
  const [details, setDetails]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [modal, setModal]       = useState(null);
  const [tab, setTab]           = useState('main');
  const [form, setForm]         = useState(EMPTY());
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [toast, setToast]       = useState('');

  const showToast = m => { setToast(m); setTimeout(() => setToast(''), 3000); };

  // ستون‌ها
  const cols = summary
    ? ['شماره', 'صیاد', 'شرح', 'ریز', 'واریز', 'برداشت', 'تاریخ دفتر', 'تاریخ بانک',
       'نوع سند', 'نوع سند فرعی', 'توضیحات', 'سریال چک', 'سال مالی', 'منبع اعتبار', 'کد درآمد', 'وضعیت']
    : ['شماره', 'صیاد', 'شرح', 'ریز', 'واریز', 'برداشت', 'تاریخ دفتر', 'تاریخ بانک',
       'نوع سند', 'نوع سند فرعی', 'توضیحات', 'اختلاف', 'بدون سند', 'خطا', 'سال مالی',
       'منبع اعتبار', 'کد درآمد', 'وضعیت'];
  const initW = cols.map((c, i) => (i === 2 ? 220 : i === 0 ? 110 : 120));
  const [colW, setColW] = useState(initW);
  const colWRef = useRef(initW);
  useEffect(() => { setColW(initW); colWRef.current = initW; }, [summary]);   // eslint-disable-line
  useEffect(() => { colWRef.current = colW; }, [colW]);
  const startResize = useCallback((idx, e) => {
    e.preventDefault(); e.stopPropagation();
    const sx = e.clientX, sw = colWRef.current[idx];
    const onMove = ev => setColW(w => { const n = [...w]; n[idx] = Math.max(40, sw + (sx - ev.clientX)); return n; });
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp);
  }, []);

  // بارگذاری گزینه‌ها (وضعیت/فیلترها/تاریخ امروز)
  useEffect(() => {
    (async () => {
      const j = await API('/options/');
      if (j.ok) { setOpts(j); setCurrentDate(c => c || j.today); }
    })();
  }, []);

  const qs = (extra = {}) => {
    const p = new URLSearchParams({
      account: account?.id || 0,
      summary: summary ? 1 : 0,
      filter: filterIdx,
      search,
      page: 1,
      ...extra,
    });
    if (currentDate) p.set('current_date', currentDate);
    if (startYear)   p.set('start_year', startYear);
    if (endYear)     p.set('end_year', endYear);
    if (fromDaftar)  p.set('from_daftar', fromDaftar);
    if (toDaftar)    p.set('to_daftar', toDaftar);
    if (fromBank)    p.set('from_bank', fromBank);
    if (toBank)      p.set('to_bank', toBank);
    if (fromNo)      p.set('from_no', fromNo);
    if (toNo)        p.set('to_no', toNo);
    return p.toString();
  };

  const load = useCallback(async (opt = {}) => {
    if (!account) return;
    setLoading(true); setError('');
    const j = await API(`/transactions/?${qs(opt)}`);
    setLoading(false);
    if (!j.ok) { setError(j.msg); return; }
    setData(prev => pager.merge(prev, j, opt.page ?? 1));
    if ((opt.page ?? 1) === 1) { setSelected(null); setDetails([]); }
  });   // eslint-disable-line

  const loadSums = async () => {
    if (!account) return;
    const p = new URLSearchParams({ account: account.id });
    if (startYear) p.set('start_year', startYear);
    if (endYear)   p.set('end_year', endYear);
    const j = await API(`/transactions/sum/?${p.toString()}`);
    if (j.ok) setSums(j.data);
  };

  // انتخاب حساب → هدر + فهرست
  const pickAccount = async (r) => {
    setAccount(r);
    const j = await API(`/accounts/${r.id}/`);
    if (j.ok) setInfo(j.data);
  };
  useEffect(() => { if (account) { load({ page: 1 }); loadSums(); } }, [account]);   // eslint-disable-line

  const pickRow = async (r) => {
    if (selected?.id === r.id) { setSelected(null); setDetails([]); return; }
    setSelected(r); setDetails([]);
    if (r.detail_count > 0) {
      const j = await API(`/transactions/${r.id}/details/`);
      if (j.ok) setDetails(j.data);
    }
  };

  const F = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openNew = () => { if (!vbf.can('BTNNEW')) return;
    setForm({ ...EMPTY(), sanad_date: currentDate, financial_year: (currentDate || '').slice(0, 4) });
    setError(''); setTab('main'); setModal('new');
  };
  const openEdit = () => { if (!vbf.can('BTNEDIT')) return;
    if (!selected) return;
    setForm({
      no: selected.no, sayad: selected.sayad,
      in_amount:  selected.flag === 0 ? selected.amount : '',
      out_amount: selected.flag === 1 ? selected.amount : '',
      daftar_date: selected.daftar_date, bank_date: selected.bank_date,
      remark: selected.remark, remark2: selected.remark2,
      financial_year: selected.financial_year,
      sanad_no: selected.sanad_no, sanad_date: selected.sanad_date,
      is_active: selected.is_active, is_final: selected.is_final, status: selected.status,
      vouch_group_id: selected.vouch_group_id, vouch_group: selected.vouch_group,
      vouch_group2_id: selected.vouch_group2_id, vouch_group2: selected.vouch_group2,
      credit_id: selected.credit_id, credit: selected.credit,
      income_id: selected.income_id, income: selected.income,
    });
    setError(''); setTab('main'); setModal('edit');
  };
  const openDel = () => { if (!vbf.can('BTNDELETE')) return; if (selected) setModal('delete'); };

  const save = async () => {
    setSaving(true); setError('');
    // کنترل «شماره فيش يا چك تکراری است» — با تأیید کاربر
    const chk = await API(`/transactions/check-no/?account=${account.id}&no=${encodeURIComponent(form.no)}${modal === 'edit' ? `&id=${selected.id}` : ''}`);
    if (chk.ok && chk.duplicate) {
      // eslint-disable-next-line no-restricted-globals
      if (!window.confirm(chk.msg)) { setSaving(false); return; }
    }
    const body = JSON.stringify({ ...form, account: account.id, current_date: currentDate });
    const j = modal === 'new'
      ? await API('/transactions/create/', { method: 'POST', body })
      : await API(`/transactions/${selected.id}/update/`, { method: 'PUT', body });
    setSaving(false);
    if (j.ok) { setModal(null); showToast(j.msg); load({ page: 1 }); loadSums(); }
    else setError(j.msg);
  };

  const doDelete = async () => {
    setSaving(true);
    const j = await API(`/transactions/${selected.id}/delete/`, { method: 'DELETE' });
    setSaving(false);
    if (j.ok) { setModal(null); showToast('حذف شد'); load({ page: 1 }); loadSums(); }
    else { setError(j.msg); setModal(null); }
  };

  /* ── حساب انتخاب نشده: صفحهٔ انتخاب حساب ── */
  if (!account) {
    return (
      <ListPage user={user} onBack={onBack} title="دریافت و پرداخت" subtitle="بخش دریافت و پرداخت — سامانه ارجان"
        toolbar={<div style={{ fontSize: '12px', color: '#666' }}
        pager={pager} loading={loading} onMore={p => load({ page: p })}>انتخاب حساب بانکی</div>}>
        <AccountPicker onPick={pickAccount} />
      </ListPage>
    );
  }

  const toolbar = (
    <>
      <div style={ui.toolBtns}>
        {vbf.can('BTNNEW') && <button onClick={openNew} style={ui.btnNew}>＋ جدید</button>}
        {vbf.can('BTNEDIT') && <button onClick={openEdit} disabled={!selected} style={{ ...ui.btnEdit, opacity: selected ? 1 : 0.4, cursor: selected ? 'pointer' : 'default' }}>✎ ویرایش</button>}
        {vbf.can('BTNDELETE') && <button onClick={openDel} disabled={!selected} style={{ ...ui.btnDel, opacity: selected ? 1 : 0.4, cursor: selected ? 'pointer' : 'default' }}>✕ حذف</button>}
        <button onClick={() => { setAccount(null); setInfo(null); setData([]); setSums(null); }} style={ui.btnItems}>🏦 تغییر حساب</button>
        <button onClick={() => setShowFilters(v => !v)} style={ui.btnItems}>⚙️ فیلترها</button>
      </div>
      <select value={filterIdx} onChange={e => { const v = Number(e.target.value); setFilterIdx(v); load({ filter: v, page: 1 }); }}
        style={{ ...ui.input, width: '220px' }} title="فیلتر آماده">
        {opts.filters.map((f, i) => <option key={i} value={i}>{f}</option>)}
      </select>
      <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
        <input type="checkbox" checked={summary} onChange={e => { setSummary(e.target.checked); load({ summary: e.target.checked ? 1 : 0, page: 1 }); }} />
        خلاصه
      </label>
      <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load({ page: 1 })}
        style={{ ...ui.searchInput, flex: 1, minWidth: '150px' }} placeholder="جستجو (مبلغ، شماره، شرح، ریز)..." />
      {loading && <span>⏳</span>}
      <CountBadge shown={data.length} total={pager.total} loading={loading} />
    </>
  );

  return (
    <>
      <ListPage user={user} onBack={onBack} title="دریافت و پرداخت" subtitle="بخش دریافت و پرداخت — سامانه ارجان"
        toolbar={toolbar} error={!modal ? error : ''}>

        <div style={ui.splitWrap}>
        {/* هدر حساب */}
        {info && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', padding: '10px 12px', margin: '0 0 8px',
            background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', fontSize: '12px' }}>
            <span><b>حساب:</b> <span style={{ fontFamily: 'monospace' }}>{toFa(info.no)}</span></span>
            <span><b>عنوان:</b> {info.title}</span>
            <span><b>بانک:</b> {info.bank} {info.branch ? `— ${info.branch}` : ''}</span>
            {info.owner && <span><b>دستگاه:</b> {info.owner}</span>}
            {info.nasim && <span><b>نسیم:</b> {toFa(info.nasim)}</span>}
            {info.chq_type && <span><b>نوع چک:</b> {info.chq_type}</span>}
            {sums && (
              <>
                <span style={{ color: '#1b5e20' }}><b>جمع واریز:</b> {fmtNum(sums.sum_in)}</span>
                <span style={{ color: '#b71c1c' }}><b>جمع برداشت:</b> {fmtNum(sums.sum_out)}</span>
                <span style={{ color: sums.balance < 0 ? '#b71c1c' : '#1b5e20' }}><b>مانده:</b> {fmtNum(sums.balance)}</span>
              </>
            )}
          </div>
        )}

        {/* فیلترهای پیشرفته */}
        {showFilters && (
          <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '10px 12px', marginBottom: '8px' }}>
            <div style={ui.row}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                <label style={ui.mlabel}>تاریخ جاری (ملاک وضعیت)</label>
                <JalaliDatePicker value={currentDate} onChange={setCurrentDate} placeholder="تاریخ جاری" />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '110px' }}>
                <label style={ui.mlabel}>از سال مالی</label>
                <input type="number" value={startYear} onChange={e => setStartYear(e.target.value)} style={ui.input} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '110px' }}>
                <label style={ui.mlabel}>تا سال مالی</label>
                <input type="number" value={endYear} onChange={e => setEndYear(e.target.value)} style={ui.input} />
              </div>
            </div>
            <div style={ui.row}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                <label style={ui.mlabel}>از تاریخ دفتر</label>
                <JalaliDatePicker value={fromDaftar} onChange={setFromDaftar} placeholder="از تاریخ دفتر" />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                <label style={ui.mlabel}>تا تاریخ دفتر</label>
                <JalaliDatePicker value={toDaftar} onChange={setToDaftar} placeholder="تا تاریخ دفتر" />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                <label style={ui.mlabel}>از تاریخ بانک</label>
                <JalaliDatePicker value={fromBank} onChange={setFromBank} placeholder="از تاریخ بانک" />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                <label style={ui.mlabel}>تا تاریخ بانک</label>
                <JalaliDatePicker value={toBank} onChange={setToBank} placeholder="تا تاریخ بانک" />
              </div>
            </div>
            <div style={ui.row}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                <label style={ui.mlabel}>از شماره</label>
                <input value={fromNo} onChange={e => setFromNo(e.target.value)} style={ui.input} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                <label style={ui.mlabel}>تا شماره</label>
                <input value={toNo} onChange={e => setToNo(e.target.value)} style={ui.input} />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                <button onClick={() => { load({ page: 1 }); loadSums(); }} style={ui.btnNew}>🔍 اعمال</button>
              </div>
            </div>
          </div>
        )}

        <div style={ui.topPane}>
        <table style={{ ...ui.table, tableLayout: 'fixed' }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
            <tr>
              {cols.map((h, i) => (
                <th key={i} style={{ ...ui.th, width: colW[i], minWidth: 40, position: 'relative', userSelect: 'none' }}>
                  {h}<span onMouseDown={e => startResize(i, e)} style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', cursor: 'col-resize' }} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((r, i) => {
              const sel = selected?.id === r.id;
              const err = !summary && r.status_error;
              const bg  = sel ? '#1a2f50' : err ? '#ffebee' : i % 2 === 0 ? '#fff' : '#f4f7fb';
              const tc  = sel ? '#fff' : err ? '#b71c1c' : '#111';
              return (
                <tr key={r.id} style={{ background: bg, cursor: 'pointer' }}
                  onClick={() => pickRow(r)} onDoubleClick={() => { setSelected(r); setTimeout(openEdit, 0); }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = '#e8f0fe'; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = bg; }}>
                  <td style={{ ...ui.td, color: tc }}>{toFa(r.no) || '•'}</td>
                  <td style={{ ...ui.td, color: tc }}>{toFa(r.sayad) || '•'}</td>
                  <td style={{ ...ui.td, textAlign: 'right', color: tc, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.remark}>{r.remark || '•'}</td>
                  <td style={{ ...ui.td, color: tc }}>{toFa(r.detail_count)}</td>
                  <td style={{ ...ui.tdNum, color: sel ? '#fff' : '#1b5e20' }}>{fmtNum(r.deposit)}</td>
                  <td style={{ ...ui.tdNum, color: sel ? '#fff' : '#b71c1c' }}>{fmtNum(r.withdraw)}</td>
                  <td style={{ ...ui.td, fontFamily: 'monospace', fontSize: '11px', color: tc }}>{r.daftar_date || '•'}</td>
                  <td style={{ ...ui.td, fontFamily: 'monospace', fontSize: '11px', color: tc }}>{r.bank_date || '•'}</td>
                  <td style={{ ...ui.td, textAlign: 'right', color: tc }}>{r.vouch_group || '•'}</td>
                  <td style={{ ...ui.td, textAlign: 'right', color: tc }}>{r.vouch_group2 || '•'}</td>
                  <td style={{ ...ui.td, textAlign: 'right', color: tc, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.remark2}>{r.remark2 || '•'}</td>
                  {!summary && <td style={{ ...ui.tdNum, color: sel ? '#fff' : (r.diff ? '#b71c1c' : '#111') }}>{fmtNum(r.diff)}</td>}
                  {!summary && <td style={{ ...ui.td, color: tc }}>{toFa(r.no_document)}</td>}
                  {!summary && <td style={{ ...ui.td, textAlign: 'right', color: sel ? '#fff' : '#b71c1c', fontSize: '11px' }}>{r.status_error || ''}</td>}
                  {summary && <td style={{ ...ui.td, color: tc }}>{toFa(r.serial_cheques) || '•'}</td>}
                  <td style={{ ...ui.td, color: tc }}>{toFa(r.financial_year)}</td>
                  <td style={{ ...ui.td, textAlign: 'right', color: tc, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.credit}>{r.credit || '•'}</td>
                  <td style={{ ...ui.td, textAlign: 'right', color: tc, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.income}>{r.income || '•'}</td>
                  <td style={{ ...ui.td, textAlign: 'right', color: tc, fontSize: '11px' }}>{r.status_name || '•'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!loading && data.length === 0 && <div style={ui.empty}>📭 سندی یافت نشد</div>}
        </div>

        {/* ریز سند انتخاب‌شده — پنل پایین، همیشه در دید */}
        <div style={ui.bottomPane}>
        {!(selected && details.length > 0) && (
          <div style={{ ...ui.empty, padding: '18px' }}>
            برای دیدن ریز سند، یک ردیف را از جدول بالا انتخاب کنید
          </div>
        )}
        {selected && details.length > 0 && (
          <div style={{ padding: '4px' }}>
            <div style={{ fontSize: '12.5px', fontWeight: 800, color: '#1a2f50', margin: '6px 2px' }}>
              ریز سند — {toFa(details.length)} ردیف
            </div>
            <div style={{ overflowX: 'auto', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
              <table style={{ ...ui.table, minWidth: '1100px' }}>
                <thead>
                  <tr>{['شرح', 'مبلغ', 'واریز', 'برداشت', 'قرارداد', 'شمارهٔ حساب گیرنده',
                    'شناسه پرداخت', 'شناسه قبض', 'توضیحات']
                    .map((h, i) => <th key={i} style={{ ...ui.th, whiteSpace: 'nowrap' }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {details.map((d, i) => (
                    <tr key={d.id} style={{ background: i % 2 === 0 ? '#fff' : '#f4f7fb' }}>
                      <td style={{ ...ui.td, textAlign: 'right' }} title={d.remark}>{d.remark || '•'}</td>
                      <td style={ui.tdNum}>{fmtNum(d.detail_amount)}</td>
                      <td style={{ ...ui.tdNum, color: '#1b5e20' }}>{fmtNum(d.deposit)}</td>
                      <td style={{ ...ui.tdNum, color: '#b71c1c' }}>{fmtNum(d.withdraw)}</td>
                      <td style={ui.td}>{toFa(d.contract_id) || '•'}</td>
                      <td style={{ ...ui.td, fontFamily: 'monospace', fontSize: '11px' }}>{toFa(d.account_no) || '•'}</td>
                      <td style={{ ...ui.td, fontFamily: 'monospace', fontSize: '11px' }}>{toFa(d.shenase_pardakht) || '•'}</td>
                      <td style={{ ...ui.td, fontFamily: 'monospace', fontSize: '11px' }}>{toFa(d.slip_id) || '•'}</td>
                      <td style={{ ...ui.td, textAlign: 'right' }} title={d.remark2}>{d.remark2 || '•'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        </div>
        </div>
      </ListPage>

      {(modal === 'new' || modal === 'edit') && (
        <div style={ui.overlay} onClick={() => setModal(null)}>
          <div style={{ ...ui.modal, maxWidth: '820px' }} onClick={e => e.stopPropagation()}>
            <div style={ui.mHeader}>
              <div style={ui.mTitle}>{modal === 'new' ? '＋ سند دریافت/پرداخت جدید' : '✎ ویرایش سند'}</div>
              <button onClick={() => setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={{ display: 'flex', gap: '6px', padding: '8px 14px 0' }}>
              {[['main', 'اطلاعات اصلی'], ['amounts', 'مبالغ و تاریخ'], ['extra', 'سند و طبقه‌بندی']].map(([k, t]) => (
                <button key={k} onClick={() => setTab(k)}
                  style={{ padding: '6px 14px', borderRadius: '8px 8px 0 0', border: '1px solid #e0e0e0',
                    borderBottom: tab === k ? '2px solid #1a2f50' : '1px solid #e0e0e0',
                    background: tab === k ? '#fff' : '#f3f5f9', cursor: 'pointer', fontFamily: 'inherit',
                    fontWeight: tab === k ? 700 : 400, fontSize: '12px' }}>{t}</button>
              ))}
            </div>
            <div style={ui.mBody}>
              {error && <div style={ui.errBox}>⚠️ {error}</div>}

              {tab === 'main' && (
                <>
                  <div style={ui.row}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                      <label style={ui.mlabel}>شماره فیش / چک</label>
                      <input value={form.no} onChange={e => F('no', e.target.value)} style={ui.input} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                      <label style={ui.mlabel}>صیاد</label>
                      <input value={form.sayad} onChange={e => F('sayad', e.target.value)} style={ui.input} />
                    </div>
                  </div>
                  <div style={ui.row}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                      <label style={ui.mlabel}>شرح</label>
                      <input value={form.remark} onChange={e => F('remark', e.target.value)} style={ui.input} />
                    </div>
                  </div>
                  <div style={ui.row}>
                    <Lookup label="متن آماده" value="" endpoint={`/lookup/remark/?account=${account.id}`}
                      onPick={r => r.display && F('remark', r.display)} />
                  </div>
                  <div style={ui.row}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                      <label style={ui.mlabel}>توضیحات</label>
                      <input value={form.remark2} onChange={e => F('remark2', e.target.value)} style={ui.input} />
                    </div>
                  </div>
                </>
              )}

              {tab === 'amounts' && (
                <>
                  <div style={ui.row}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                      <label style={ui.mlabel}>واریز</label>
                      <input type="number" value={form.in_amount}
                        onChange={e => setForm(f => ({ ...f, in_amount: e.target.value, out_amount: e.target.value ? '' : f.out_amount }))}
                        style={ui.input} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                      <label style={ui.mlabel}>برداشت</label>
                      <input type="number" value={form.out_amount}
                        onChange={e => setForm(f => ({ ...f, out_amount: e.target.value, in_amount: e.target.value ? '' : f.in_amount }))}
                        style={ui.input} />
                    </div>
                  </div>
                  <div style={{ fontSize: '11px', color: '#888', margin: '-4px 0 6px' }}>
                    واریز و برداشت هر دو باهم نمیتوانند مقدار داشته باشند.
                  </div>
                  <div style={ui.row}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                      <label style={ui.mlabel}>تاریخ دفتر</label>
                      <JalaliDatePicker value={form.daftar_date} onChange={v => F('daftar_date', v)} placeholder="تاریخ دفتر" />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                      <label style={ui.mlabel}>تاریخ بانک</label>
                      <JalaliDatePicker value={form.bank_date} onChange={v => F('bank_date', v)} placeholder="تاریخ بانک" />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '120px' }}>
                      <label style={ui.mlabel}>سال مالی</label>
                      <input type="number" value={form.financial_year} onChange={e => F('financial_year', e.target.value)} style={ui.input} />
                    </div>
                  </div>
                  <div style={ui.row}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                      <label style={ui.mlabel}>وضعیت</label>
                      <select value={form.status} onChange={e => F('status', Number(e.target.value))} style={ui.input}>
                        {opts.status.map((s2, i) => <option key={i} value={i}>{s2}</option>)}
                      </select>
                    </div>
                  </div>
                </>
              )}

              {tab === 'extra' && (
                <>
                  <div style={ui.row}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                      <label style={ui.mlabel}>شماره سند</label>
                      <input value={form.sanad_no} onChange={e => F('sanad_no', e.target.value)} style={ui.input} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                      <label style={ui.mlabel}>تاریخ سند</label>
                      <JalaliDatePicker value={form.sanad_date} onChange={v => F('sanad_date', v)} placeholder="تاریخ سند" />
                    </div>
                  </div>
                  <div style={ui.row}>
                    <Lookup label="نوع سند" value={form.vouch_group} endpoint="/lookup/vouch-type-group/"
                      onPick={r => setForm(f => ({ ...f, vouch_group_id: r.id, vouch_group: r.display }))} />
                    <Lookup label="نوع سند فرعی" value={form.vouch_group2} endpoint="/lookup/vouch-type-group/"
                      onPick={r => setForm(f => ({ ...f, vouch_group2_id: r.id, vouch_group2: r.display }))} />
                  </div>
                  <div style={ui.row}>
                    <Lookup label="منبع اعتبار" value={form.credit} endpoint="/lookup/credit/"
                      onPick={r => setForm(f => ({ ...f, credit_id: r.id, credit: r.display }))} />
                  </div>
                  <div style={ui.row}>
                    <Lookup label="کد درآمد" value={form.income} endpoint="/lookup/income/"
                      onPick={r => setForm(f => ({ ...f, income_id: r.id, income: r.display }))} />
                  </div>
                  <div style={{ ...ui.row, gap: '18px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!form.is_active} onChange={e => F('is_active', e.target.checked ? 1 : 0)} />
                      فعال
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!form.is_final} onChange={e => F('is_final', e.target.checked ? 1 : 0)} />
                      بسته (نهایی)
                    </label>
                  </div>
                </>
              )}
            </div>
            <div style={ui.mFooter}>
              <button onClick={() => { setModal(null); setError(''); }} style={ui.btnCancel}>انصراف</button>
              {vbf.can('BTNOK') && <button onClick={save} disabled={saving} style={ui.btnSave}>{saving ? '⏳ ...' : '💾 ذخیره'}</button>}
            </div>
          </div>
        </div>
      )}

      {modal === 'delete' && (
        <div style={ui.overlay} onClick={() => setModal(null)}>
          <div style={{ ...ui.modal, maxWidth: '460px' }} onClick={e => e.stopPropagation()}>
            <div style={{ ...ui.mHeader, background: 'linear-gradient(135deg,#b71c1c,#c62828)' }}>
              <div style={ui.mTitle}>⚠️ تأیید حذف</div>
              <button onClick={() => setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={{ ...ui.mBody, textAlign: 'center', padding: '26px' }}>
              <div style={{ fontSize: '34px', marginBottom: '10px' }}>🗑️</div>
              شماره {toFa(selected?.no)}<br />
              {fmtNum(selected?.amount)} ریال<br />{selected?.remark}
            </div>
            <div style={ui.mFooter}>
              <button onClick={() => setModal(null)} style={ui.btnCancel}>خیر</button>
              {vbf.can('BTNDELETE') && <button onClick={doDelete} disabled={saving} style={{ ...ui.btnSave, background: '#b71c1c' }}>{saving ? '⏳...' : 'بله، حذف'}</button>}
            </div>
          </div>
        </div>
      )}

      {toast && <div style={ui.toast}>{toast}</div>}
    </>
  );
}
